import { useEffect, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { UIProvider } from "@/contexts/UIContext";
import { CalendarLayout } from "@/components/layout/CalendarLayout";
import { MasterLayout } from "@/components/layout/MasterLayout";
import { useRealtimeProfile } from "@/hooks/useRealtimeProfile";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { RequireRole } from "./components/auth/RequireRole";
import MarketingOnlyWidgets from "@/components/marketing/MarketingOnlyWidgets";
import MessageIanEmailCapture from "@/components/marketing/MessageIanEmailCapture";
import CookieConsent from "@/components/marketing/CookieConsent";
import { intensiveSimulationAvailable } from '@/lib/intensiveSimulation';

// Critical pages - keep static for SEO and FCP
import BestDriveBath from "./pages/BestDriveBath";
import DrivingLessonsBath from "./pages/DrivingLessonsBath";
import NotFound from "./pages/NotFound";

// Loading fallback for lazy components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Lazy load all other pages for code splitting
const LandingPage = lazy(() => import("./pages/LandingPage").then(m => ({ default: m.LandingPage })));
const AppShowcase = lazy(() => import("./pages/AppShowcase").then(m => ({ default: m.AppShowcase })));
const BDPT_ProgressTrackerPage_V2 = lazy(() => import("./pages/BDPT_ProgressTrackerPage_V2").then(m => ({ default: m.BDPT_ProgressTrackerPage_V2 })));
const StudentHome = lazy(() => import("./pages/StudentHome"));
const StudentLogin = lazy(() => import("./pages/StudentLogin"));
const StudentSelfRegister = lazy(() => import("./pages/StudentSelfRegister"));

const StudentFocus = lazy(() => import("./pages/StudentFocus"));
const StudentHeatmap = lazy(() => import("./pages/StudentHeatmap"));
const StudentProgressGraph = lazy(() => import("./pages/StudentProgressGraph"));
const PassPrediction = lazy(() => import("./pages/PassPrediction"));
const StudentSkillDetail = lazy(() => import("./pages/StudentSkillDetail"));
// REMOVED: Message pages (messaging feature removed)
// const StudentMessages = lazy(() => import("./pages/StudentMessages"));
// const Messages = lazy(() => import("./pages/Messages"));
const Auth = lazy(() => import("./pages/Auth"));
const Skills = lazy(() => import("./pages/Skills"));
const Mindfulness = lazy(() => import("./pages/Mindfulness"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Profile = lazy(() => import("./pages/Profile"));
const InstructorNew = lazy(() => import("./pages/InstructorNew"));
const StudentDetail = lazy(() => import("./pages/StudentDetail"));
const StudentProfile = lazy(() => import("./pages/StudentProfile"));

const AppDownload = lazy(() => import("./pages/AppDownload"));
const Unauthorised = lazy(() => import("./pages/Unauthorised"));
const DVSASkills = lazy(() => import("./pages/DVSASkills").then(m => ({ default: m.DVSASkills })));
const DVSASkillDetail = lazy(() => import("./pages/DVSASkillDetail").then(m => ({ default: m.DVSASkillDetail })));
const InstructorDVSASkills = lazy(() => import("./pages/InstructorDVSASkills").then(m => ({ default: m.InstructorDVSASkills })));
const InstructorSimplifiedSkillsPage = lazy(() => import("./pages/InstructorSimplifiedSkillsPage"));
const InstructorDVSASkillDetail = lazy(() => import("./pages/InstructorDVSASkillDetail").then(m => ({ default: m.InstructorDVSASkillDetail })));
const InstructorSkills = lazy(() => import("./pages/InstructorSkills"));
const InstructorStudentProgress = lazy(() => import("./pages/InstructorStudentProgress"));
const InstructorDiary = lazy(() => import("./pages/InstructorDiary"));
const InstructorCalendar = lazy(() => import("./pages/InstructorCalendar"));
const IntensiveOfferSimulation = lazy(() => import('./components/instructor/IntensiveOfferSimulation'));
const PrivateOffers = lazy(() => import('./pages/PrivateOffers'));
const IntensiveCourseManagement = lazy(() => import('./pages/IntensiveCourseManagement'));
const OfferReview = lazy(() => import('./pages/OfferReview'));
const BookNewAppointment = lazy(() => import("./pages/BookNewAppointment"));
const AddStudent = lazy(() => import("./pages/AddStudent"));
// REMOVED: Instructor messages (messaging feature removed)
// const InstructorMessages = lazy(() => import("./pages/InstructorMessages"));
const InstructorMockTestPage = lazy(() => import("./pages/InstructorMockTestPage"));
const InstructorMockTests = lazy(() => import("./pages/InstructorMockTests"));
const StudentMockTests = lazy(() => import("./pages/StudentMockTests"));

const CarControls = lazy(() => import("./pages/CarControls").then(m => ({ default: m.CarControls })));
const Terms = lazy(() => import("./pages/Terms"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const InstructorSettings = lazy(() => import("./pages/InstructorSettings"));
const AdminMagicLink = lazy(() => import("./pages/AdminMagicLink"));
const MagicAuth = lazy(() => import("./pages/MagicAuth"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));
const AdminTools = lazy(() => import("./pages/AdminTools"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EmailVerificationHandler = lazy(() => import("./components/auth/EmailVerificationHandler"));
const QRStudentRegistration = lazy(() => import("./pages/QRStudentRegistration").then(m => ({ default: m.QRStudentRegistration })));
const StudentRegister = lazy(() => import("./pages/StudentRegister"));

const InstructorNotes = lazy(() => import("./pages/InstructorNotes"));
const InstructorNewRegistrations = lazy(() => import("./pages/InstructorNewRegistrations"));
const InstructorDaySchedule = lazy(() => import("./pages/InstructorDaySchedule"));
const InstructorAccount = lazy(() => import("./pages/InstructorAccount"));
const TellMeShowMe = lazy(() => import("./pages/TellMeShowMe"));
const StudentProgressReport = lazy(() => import("./pages/StudentProgressReport"));
const LessonPlanEditor = lazy(() => import("./pages/LessonPlanEditor"));
const LessonPlanHistory = lazy(() => import("./pages/LessonPlanHistory"));
const StudentLessonPlans = lazy(() => import("./pages/StudentLessonPlans"));
const StudentBookLesson = lazy(() => import("./pages/StudentBookLesson"));
const StudentBookingSuccess = lazy(() => import("./pages/StudentBookingSuccess"));
const InstructorPayments = lazy(() => import("./pages/InstructorPayments"));
const StudentPaymentDetail = lazy(() => import("./pages/StudentPaymentDetail"));
const InstructorDrivingTests = lazy(() => import("./pages/InstructorDrivingTests"));
const InstructorProgressReportView = lazy(() => import("./pages/InstructorProgressReportView"));
const InstructorBackupRestore = lazy(() => import("./pages/InstructorBackupRestore"));
const InstructorApiKeys = lazy(() => import("./pages/InstructorApiKeys"));
const InstructorTerms = lazy(() => import("./pages/InstructorTerms"));
const CourseTerms = lazy(() => import("./pages/CourseTerms"));

const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const AuditReport = lazy(() => import("./pages/AuditReport"));
const SEOAuditReport = lazy(() => import("./pages/SEOAuditReport"));
const ParentLesson = lazy(() => import("./pages/ParentLesson"));
const ComplaintsPolicy = lazy(() => import("./pages/policies/ComplaintsPolicy"));
const SafeguardingPolicy = lazy(() => import("./pages/policies/SafeguardingPolicy"));
const EDIPolicy = lazy(() => import("./pages/policies/EDIPolicy"));
const AccessibilityPolicy = lazy(() => import("./pages/policies/AccessibilityPolicy"));
const YourInstructor = lazy(() => import("./pages/YourInstructor"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));


const Prices = lazy(() => import("./pages/Prices"));

const IntensiveDrivingCoursesBath = lazy(() => import("./pages/IntensiveDrivingCoursesBath"));
const IntensiveDrivingCoursesChippenham = lazy(() => import("./pages/IntensiveDrivingCoursesChippenham"));
const IntensiveDrivingCoursesKingswood = lazy(() => import("./pages/IntensiveDrivingCoursesKingswood"));
const About = lazy(() => import("./pages/About"));
const AboutIan = lazy(() => import("./pages/AboutIan"));
const AreasCovered = lazy(() => import("./pages/AreasCovered"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiesPolicy = lazy(() => import("./pages/CookiesPolicy"));
const MockTests = lazy(() => import("./pages/MockTests"));
const ParentLearning = lazy(() => import("./pages/ParentLearning"));
const GBPChecklist = lazy(() => import("./pages/GBPChecklist"));

const DrivingInstructorTrainingBath = lazy(() => import("./pages/DrivingInstructorTrainingBath"));
const BecomeDrivingInstructor = lazy(() => import("./pages/BecomeDrivingInstructor"));
const IsBecomingInstructorWorthItBath = lazy(() => import("./pages/IsBecomingInstructorWorthItBath"));
const NervousDrivers = lazy(() => import("./pages/NervousDrivers"));
const LearningDifficulties = lazy(() => import("./pages/LearningDifficulties"));
const RefresherCourses = lazy(() => import("./pages/RefresherCourses"));
const ClearPathQuiz = lazy(() => import("./pages/ClearPathQuiz"));
const ClearPathDetail = lazy(() => import("./pages/ClearPathDetail"));
const WhyBestDrive = lazy(() => import("./pages/WhyBestDrive"));
const IanPerryDrivingInstructor = lazy(() => import("./pages/IanPerryDrivingInstructor"));
const NewDriverEssentials = lazy(() => import("./pages/NewDriverEssentials"));
const AfterYouPass = lazy(() => import("./pages/AfterYouPass"));
const BeforeYourTestChippenhamKingswood = lazy(() => import("./pages/BeforeYourTestChippenhamKingswood"));
const YourDrivingJourney = lazy(() => import("./pages/YourDrivingJourney"));

const BookNow = lazy(() => import("./pages/BookNow"));
const BookNowSuccess = lazy(() => import("./pages/BookNowSuccess"));
const BookingThankYou = lazy(() => import("./pages/BookingThankYou"));

// Area pages
const BathArea = lazy(() => import("./pages/areas/BathArea"));
const ChippenhamArea = lazy(() => import("./pages/areas/ChippenhamArea"));
const KingswoodArea = lazy(() => import("./pages/areas/KingswoodArea"));
const BristolArea = lazy(() => import("./pages/areas/BristolArea"));
const IntensiveDrivingCoursesBristol = lazy(() => import("./pages/IntensiveDrivingCoursesBristol"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { 
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      retry: (failureCount, error: any) => {
        if (error?.status === 401 || error?.status === 403) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
    },
    mutations: { 
      retry: 1,
      onError: (error: any) => {
        if (error?.status === 401) {
          console.warn('Authentication error in mutation');
        }
      }
    }
  }
});

function AppContent() {
  useRealtimeProfile();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <MessageIanEmailCapture />
      <CookieConsent />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Root route - critical for FCP, keep static */}
          <Route path="/" element={<DrivingLessonsBath />} />
          <Route path="/driving-lessons" element={<Navigate to="/" replace />} />
          
          {/* Calendar route outside MasterLayout */}
          <Route path="/instructor-calendar" element={<RequireRole role="instructor"><CalendarLayout><InstructorCalendar /></CalendarLayout></RequireRole>} />
          {intensiveSimulationAvailable() && <Route path="/intensive-offer-test" element={<IntensiveOfferSimulation />} />}
          <Route path="/instructor/private-offers" element={<RequireRole role="instructor"><MasterLayout><PrivateOffers /></MasterLayout></RequireRole>} />
          <Route path="/instructor/intensive-courses" element={<RequireRole role="instructor"><MasterLayout><IntensiveCourseManagement /></MasterLayout></RequireRole>} />
          <Route path="/offer/:token" element={<OfferReview />} />
          <Route path="/instructor/book-appointment" element={<RequireRole role="instructor"><BookNewAppointment /></RequireRole>} />
          
          {/* Marketing pages - Public */}
          <Route path="/showcase" element={<AppShowcase />} />
          <Route path="/progress-tracker" element={<BDPT_ProgressTrackerPage_V2 />} />
          <Route path="/progress-tracker-v2" element={<BDPT_ProgressTrackerPage_V2 />} />
          <Route path="/progress-tracker-lp" element={<BDPT_ProgressTrackerPage_V2 />} />
          <Route path="/bath" element={<DrivingLessonsBath />} />
          <Route path="/intensive-driving-courses" element={<BestDriveBath />} />
          <Route path="/intensive-driving-courses-cheltenham" element={<BestDriveBath key="cheltenham" courseLocation="Cheltenham" />} />

          <Route path="/intensives" element={<Navigate to="/intensive-driving-courses-bath" replace />} />
          <Route path="/intensive-courses-bath" element={<Navigate to="/intensive-driving-courses-bath" replace />} />
          <Route path="/audit-report" element={<RequireRole role="instructor"><AuditReport /></RequireRole>} />
          <Route path="/seo-audit-report" element={<RequireRole role="instructor"><SEOAuditReport /></RequireRole>} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/how-much-are-driving-lessons-in-bath-2025" element={<Navigate to="/blog/driving-lesson-prices-bath" replace />} />
          <Route path="/blog/bath-driving-test-routes-2025" element={<Navigate to="/blog/bath-driving-test-preparation" replace />} />
          <Route path="/blog/best-cars-learning-drive-2025" element={<Navigate to="/blog/best-cars-for-learner-drivers" replace />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          
          <Route path="/prices" element={<Prices />} />
          <Route path="/courses/:slug" element={<Navigate to="/prices" replace />} />
          <Route path="/book-driving-lessons" element={<Navigate to="/book-now" replace />} />
          <Route path="/intensive-driving-courses-bath" element={<IntensiveDrivingCoursesBath />} />
          <Route path="/intensive-driving-courses-chippenham" element={<IntensiveDrivingCoursesChippenham />} />
          <Route path="/intensive-driving-courses-kingswood" element={<IntensiveDrivingCoursesKingswood />} />
          <Route path="/fast-track-driving-courses" element={<Navigate to="/intensive-driving-courses-bath" replace />} />
          <Route path="/apply" element={<Navigate to="/intensive-driving-courses-bath" replace />} />
          <Route path="/about" element={<Navigate to="/about-ian" replace />} />
          <Route path="/about-ian" element={<AboutIan />} />
          <Route path="/areas-covered" element={<AreasCovered />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookies-policy" element={<CookiesPolicy />} />
          <Route path="/mock-tests" element={<MockTests />} />
          <Route path="/parent-learning" element={<ParentLearning />} />
          <Route path="/gbp-checklist" element={<GBPChecklist />} />
          
          <Route path="/driving-instructor-training-bath" element={<DrivingInstructorTrainingBath />} />
          <Route path="/become-a-driving-instructor" element={<BecomeDrivingInstructor />} />
          <Route path="/instructor-training-worth-it" element={<IsBecomingInstructorWorthItBath />} />
          <Route path="/is-becoming-a-driving-instructor-worth-it-bath" element={<Navigate to="/instructor-training-worth-it" replace />} />
          <Route path="/nervous-drivers" element={<NervousDrivers />} />
          <Route path="/learning-difficulties" element={<LearningDifficulties />} />
          <Route path="/refresher-courses" element={<RefresherCourses />} />
          <Route path="/clear-path-quiz" element={<ClearPathQuiz />} />
          <Route path="/clear-path/:pathType" element={<ClearPathDetail />} />
          <Route path="/why-best-drive" element={<WhyBestDrive />} />
          <Route path="/why-trust-best-drive" element={<Navigate to="/why-best-drive" replace />} />
          <Route path="/ian-perry-driving-instructor" element={<IanPerryDrivingInstructor />} />
          <Route path="/new-driver-essentials" element={<NewDriverEssentials />} />
          <Route path="/after-you-pass" element={<AfterYouPass />} />
          <Route path="/your-driving-journey" element={<YourDrivingJourney />} />
          <Route path="/before-your-test" element={<BeforeYourTestChippenhamKingswood />} />
          <Route path="/before-your-test-chippenham-kingswood" element={<Navigate to="/before-your-test" replace />} />
          <Route path="/test-day-car-hire" element={<Navigate to="/intensive-driving-courses-bath" replace />} />
          <Route path="/book-now" element={<BookNow />} />
          <Route path="/book-from-reviews" element={<Navigate to="/book-now?ref=reviews" replace />} />
          <Route path="/book-now/success" element={<BookNowSuccess />} />
          <Route path="/prices/booking-thank-you" element={<BookingThankYou />} />
          <Route path="/booking-thank-you" element={<BookingThankYou />} />
           
          {/* Area pages */}
          <Route path="/areas/bath" element={<BathArea />} />
          <Route path="/areas/chippenham" element={<ChippenhamArea />} />
          <Route path="/areas/kingswood" element={<KingswoodArea />} />
          <Route path="/areas/bristol" element={<BristolArea />} />
          <Route path="/driving-lessons-bristol" element={<Navigate to="/areas/bristol" replace />} />
          <Route path="/intensive-driving-courses-bristol" element={<IntensiveDrivingCoursesBristol />} />
          <Route path="/nervous-driver-lessons-bristol" element={<Navigate to="/areas/bristol" replace />} />
          <Route path="/driving-instructor-training-bristol" element={<Navigate to="/areas/bristol" replace />} />

          {/* Policy Pages - Public */}
          <Route path="/intensive-course-terms" element={<CourseTerms />} />
          <Route path="/policies/complaints" element={<ComplaintsPolicy />} />
          <Route path="/policies/safeguarding" element={<SafeguardingPolicy />} />
          <Route path="/policies/edi" element={<EDIPolicy />} />
          <Route path="/policies/accessibility" element={<AccessibilityPolicy />} />
          
          {/* Parent Portal */}
          <Route path="/parent-dashboard" element={<ParentDashboard />} />
          <Route path="/parent-lesson/:lessonId" element={<ParentLesson />} />
          
          {/* App routes */}
          <Route path="/landing" element={<MasterLayout><LandingPage /></MasterLayout>} />
          <Route path="/student-login" element={<StudentLogin />} />
          <Route path="/student-register" element={<StudentSelfRegister />} />
          <Route path="/student/register" element={<MasterLayout><StudentRegister /></MasterLayout>} />

          <Route path="/student-home" element={<MasterLayout><StudentHome /></MasterLayout>} />
          <Route path="/student/mock-tests" element={<MasterLayout><StudentMockTests /></MasterLayout>} />
          <Route path="/student/focus" element={<MasterLayout><StudentFocus /></MasterLayout>} />
          <Route path="/student/heatmap" element={<MasterLayout><StudentHeatmap /></MasterLayout>} />
          <Route path="/student-progress-graph" element={<MasterLayout><StudentProgressGraph /></MasterLayout>} />
          <Route path="/pass-prediction" element={<MasterLayout><PassPrediction /></MasterLayout>} />
          <Route path="/student/skill/:slug" element={<MasterLayout><StudentSkillDetail /></MasterLayout>} />
          <Route path="/student/your-instructor" element={<MasterLayout><YourInstructor /></MasterLayout>} />
          
          {/* REMOVED: Messages routes (messaging feature removed) */}
          
          <Route path="/auth" element={<MasterLayout><Auth /></MasterLayout>} />
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/sign-in" element={<Navigate to="/auth" replace />} />
          <Route path="/magic-auth" element={<MasterLayout><MagicAuth /></MasterLayout>} />
          <Route path="/auth/verify" element={<MasterLayout><EmailVerificationHandler /></MasterLayout>} />
          <Route path="/verify" element={<MasterLayout><AuthVerify /></MasterLayout>} />
          <Route path="/set-password" element={<MasterLayout><SetPassword /></MasterLayout>} />
          <Route path="/reset-password" element={<MasterLayout><ResetPassword /></MasterLayout>} />
          <Route path="/qr-register/:instructorId" element={<MasterLayout><QRStudentRegistration /></MasterLayout>} />
          <Route path="/skills" element={<MasterLayout><Skills /></MasterLayout>} />
          <Route path="/mindfulness" element={<MasterLayout><Mindfulness /></MasterLayout>} />
          <Route path="/instructor/skills" element={<RequireRole role="instructor"><MasterLayout><InstructorSkills /></MasterLayout></RequireRole>} />
          <Route path="/dvsa-skills" element={<MasterLayout><DVSASkills /></MasterLayout>} />
          <Route path="/dvsa-skills/:skillId" element={<MasterLayout><DVSASkillDetail /></MasterLayout>} />
          <Route path="/achievements" element={<MasterLayout><Achievements /></MasterLayout>} />
          <Route path="/car-controls" element={<MasterLayout><CarControls /></MasterLayout>} />
          <Route path="/tell-me-show-me" element={<MasterLayout><TellMeShowMe /></MasterLayout>} />
          <Route path="/student/progress-report" element={<MasterLayout><StudentProgressReport /></MasterLayout>} />
          <Route path="/profile" element={<MasterLayout><Profile /></MasterLayout>} />
          <Route path="/student-profile" element={<MasterLayout><StudentProfile /></MasterLayout>} />
          <Route path="/instructor" element={<RequireRole role="instructor"><MasterLayout><InstructorNew /></MasterLayout></RequireRole>} />
          <Route path="/instructor/student/new" element={<RequireRole role="instructor"><MasterLayout><AddStudent /></MasterLayout></RequireRole>} />
          <Route path="/instructor-diary" element={<RequireRole role="instructor"><MasterLayout><InstructorDiary /></MasterLayout></RequireRole>} />
          <Route path="/instructor/driving-tests" element={<RequireRole role="instructor"><MasterLayout><InstructorDrivingTests /></MasterLayout></RequireRole>} />
          <Route path="/instructor/progress" element={<RequireRole role="instructor"><MasterLayout><InstructorStudentProgress /></MasterLayout></RequireRole>} />
          <Route path="/instructor/students" element={<RequireRole role="instructor"><MasterLayout><InstructorStudentProgress /></MasterLayout></RequireRole>} />
          <Route path="/instructor/progress-report/:reportId" element={<RequireRole role="instructor"><MasterLayout><InstructorProgressReportView /></MasterLayout></RequireRole>} />
          <Route path="/instructor/notes" element={<RequireRole role="instructor"><MasterLayout><InstructorNotes /></MasterLayout></RequireRole>} />
          <Route path="/instructor/new-registrations" element={<RequireRole role="instructor"><MasterLayout><InstructorNewRegistrations /></MasterLayout></RequireRole>} />
          <Route path="/instructor/day-schedule" element={<RequireRole role="instructor"><MasterLayout><InstructorDaySchedule /></MasterLayout></RequireRole>} />
          <Route path="/instructor/payments" element={<RequireRole role="instructor"><MasterLayout><InstructorPayments /></MasterLayout></RequireRole>} />
          <Route path="/instructor/payments/:studentId" element={<RequireRole role="instructor"><MasterLayout><StudentPaymentDetail /></MasterLayout></RequireRole>} />
          <Route path="/instructor/backup" element={<RequireRole role="instructor"><MasterLayout><InstructorBackupRestore /></MasterLayout></RequireRole>} />
          <Route path="/instructor/student/:studentId" element={<RequireRole role="instructor"><MasterLayout><StudentDetail /></MasterLayout></RequireRole>} />
          <Route path="/instructor/mock-test/:studentId" element={<RequireRole role="instructor"><MasterLayout><InstructorMockTestPage /></MasterLayout></RequireRole>} />
          <Route path="/instructor/mock-tests/:studentId" element={<RequireRole role="instructor"><MasterLayout><InstructorMockTests /></MasterLayout></RequireRole>} />
          <Route path="/instructor/student/:studentId/dvsa-skills" element={<RequireRole role="instructor"><MasterLayout><InstructorSimplifiedSkillsPage /></MasterLayout></RequireRole>} />
          <Route path="/instructor/student/:studentId/dvsa-skills-legacy" element={<RequireRole role="instructor"><MasterLayout><InstructorDVSASkills /></MasterLayout></RequireRole>} />
          <Route path="/instructor/student/:studentId/skill/:skillId" element={<RequireRole role="instructor"><MasterLayout><InstructorDVSASkillDetail /></MasterLayout></RequireRole>} />
          <Route path="/instructor/student/:studentId/dvsa-skills/:skillId" element={<RequireRole role="instructor"><MasterLayout><InstructorDVSASkillDetail /></MasterLayout></RequireRole>} />
          <Route path="/instructor/lesson-plan/:planId" element={<RequireRole role="instructor"><MasterLayout><LessonPlanEditor /></MasterLayout></RequireRole>} />
          <Route path="/instructor/lesson-plan-history/:studentId" element={<RequireRole role="instructor"><MasterLayout><LessonPlanHistory /></MasterLayout></RequireRole>} />
          <Route path="/instructor/settings" element={<RequireRole role="instructor"><MasterLayout><InstructorSettings /></MasterLayout></RequireRole>} />
          <Route path="/instructor/account" element={<RequireRole role="instructor"><MasterLayout><InstructorAccount /></MasterLayout></RequireRole>} />
          <Route path="/instructor/terms" element={<RequireRole role="instructor"><MasterLayout><InstructorTerms /></MasterLayout></RequireRole>} />
          <Route path="/instructor/api-keys" element={<RequireRole role="instructor"><MasterLayout><InstructorApiKeys /></MasterLayout></RequireRole>} />
          <Route path="/student/lesson-plans" element={<MasterLayout><StudentLessonPlans /></MasterLayout>} />
          <Route path="/student/book" element={<MasterLayout><StudentBookLesson /></MasterLayout>} />
          <Route path="/student/book/success" element={<MasterLayout><StudentBookingSuccess /></MasterLayout>} />
          
          
          <Route path="/app-download" element={<MasterLayout><AppDownload /></MasterLayout>} />
          <Route path="/account-settings" element={<MasterLayout><AccountSettings /></MasterLayout>} />
          <Route path="/terms" element={<MasterLayout><Terms /></MasterLayout>} />
          <Route path="/admin" element={<Navigate to="/instructor" replace />} />
          <Route path="/admin/magic-link" element={<RequireRole role="admin"><MasterLayout><AdminMagicLink /></MasterLayout></RequireRole>} />
          <Route path="/admin/tools" element={<RequireRole role="admin"><MasterLayout><AdminTools /></MasterLayout></RequireRole>} />
          
          <Route path="/unauthorised" element={<Unauthorised />} />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      
    </BrowserRouter>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <UIProvider>
          <Toaster />
          <Sonner />
          <AppContent />
        </UIProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
